/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase typed boundary */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, ExternalLink, Tags, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  workspaceId: string;
  /** Selected framework_topic_ids. */
  value: string[];
  onChange: (topicIds: string[]) => void;
  /** Compact = chips inline + small "Add topics" trigger; default shows full row. */
  compact?: boolean;
}

type FrameworkTopic = { id: string; label: string; framework_column_id: string };
type FrameworkColumn = { id: string; label: string; position: number };

/**
 * Corpus document Topic picker. Source of truth: `content_framework_topics`
 * for the active workspace (the Codex Framework Topic Tree).
 *
 * Users cannot create topics here — only pick from existing Codex Topics.
 * See mem://features/topics-are-codex-driven.md.
 */
export function CorpusTopicPicker({ workspaceId, value, onChange, compact }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [topics, setTopics] = useState<FrameworkTopic[]>([]);
  const [columns, setColumns] = useState<FrameworkColumn[]>([]);

  useEffect(() => {
    if (!workspaceId) return;
    void (async () => {
      const [topicsRes, columnsRes] = await Promise.all([
        supabase
          .from("content_framework_topics")
          .select("id, label, framework_column_id")
          .eq("workspace_id", workspaceId)
          .order("position", { ascending: true }),
        supabase
          .from("content_framework_columns")
          .select("id, label, position")
          .eq("workspace_id", workspaceId)
          .order("position", { ascending: true }),
      ]);
      setTopics(((topicsRes.data ?? []) as any[]).map((r) => ({
        id: r.id,
        label: r.label,
        framework_column_id: r.framework_column_id,
      })));
      setColumns(((columnsRes.data ?? []) as any[]).map((r) => ({
        id: r.id,
        label: r.label,
        position: r.position,
      })));
    })();
  }, [workspaceId]);

  const topicById = useMemo(() => {
    const m = new Map<string, FrameworkTopic>();
    topics.forEach((t) => m.set(t.id, t));
    return m;
  }, [topics]);

  const isSelected = (id: string) => value.includes(id);

  const toggle = (id: string) => {
    if (isSelected(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
    setQuery("");
  };

  const remove = (id: string) => onChange(value.filter((v) => v !== id));

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchTopics = q
      ? topics.filter((t) => t.label.toLowerCase().includes(q))
      : topics;
    return columns
      .map((col) => ({
        column: col,
        topics: matchTopics.filter((t) => t.framework_column_id === col.id),
      }))
      .filter((g) => g.topics.length > 0);
  }, [columns, topics, query]);

  const totalMatched = grouped.reduce((acc, g) => acc + g.topics.length, 0);

  const triggerLabel = compact
    ? value.length > 0
      ? `${value.length}`
      : "Add"
    : value.length > 0
      ? `${value.length} topic${value.length === 1 ? "" : "s"} selected`
      : "Add topics…";

  return (
    <div className={cn("space-y-1.5", compact && "space-y-1")}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => {
            const t = topicById.get(id);
            const label = t?.label ?? "Unknown topic";
            return (
              <Badge key={id} variant="secondary" className="gap-1 pr-1 text-[11px]">
                {label}
                <button
                  type="button"
                  onClick={() => remove(id)}
                  className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
                  aria-label={`Remove ${label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            size={compact ? "sm" : "default"}
            className={cn(
              "justify-between font-normal text-muted-foreground",
              compact ? "h-7 px-2 text-xs" : "w-full"
            )}
          >
            <span className="inline-flex items-center gap-1.5">
              <Tags className="h-3.5 w-3.5" />
              {triggerLabel}
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search topics…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {totalMatched === 0 ? (
                <CommandEmpty>
                  {topics.length === 0
                    ? "No topics in this workspace's Codex Topic Tree yet."
                    : "No matching topics."}
                </CommandEmpty>
              ) : (
                grouped.map((g) => (
                  <CommandGroup key={g.column.id} heading={g.column.label}>
                    {g.topics.map((t) => {
                      const selected = isSelected(t.id);
                      return (
                        <CommandItem key={t.id} value={t.label} onSelect={() => toggle(t.id)}>
                          <Check
                            className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")}
                          />
                          {t.label}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))
              )}
            </CommandList>
            <div className="border-t border-border p-1">
              <Link
                to="/composer?bottomDock=codex&codexTab=framework"
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <ExternalLink className="h-3 w-3" />
                Manage in Codex → Framework
              </Link>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
